<template>
  <span v-html="markdownToHtml" />
</template>

<script setup>
/**
 * Use this component to render markdown content in a Vue SFC.
 * Use only for complex cases where the built-in markdown renderer in MD files is not enough
 * or you need a reusable component with props.
 * There's a POC to include md files in another md page,
 * see https://github.com/dialpad/dialtone/commit/8498ffaf89f2b305be33689b6b0ae34724ff0878
 */

import { computed } from 'vue';
import markdownIt from 'markdown-it';

const props = defineProps({
  markdown: {
    type: String,
    required: true,
  },
});

const markdownToHtml = computed(() => {
  /**
   * Fix multi-line HTML tags from JSDoc comments.
   *
   * Problem: JSDoc comments can have HTML tags split across multiple lines like:
   *   <a
   *     class="d-link"
   *     href="..."
   *   >
   *
   * Solution: Collapse all whitespace (including newlines) within HTML tags into single spaces.
   * This transforms the above into: <a class="d-link" href="...">
   */
  const normalizedMarkdown = props.markdown.replace(
    /<(\w+)([^>]*)>/gs,
    (match, tagName, attributes) => {
      // Replace all whitespace (spaces, tabs, newlines) with a single space
      const cleanAttributes = attributes.replace(/\s+/g, ' ').trim();

      // Reconstruct the tag: <tagName attributes> or <tagName> if no attributes
      return cleanAttributes ? `<${tagName} ${cleanAttributes}>` : `<${tagName}>`;
    },
  );

  // Convert markdown to HTML (with HTML tags enabled)
  const md = new markdownIt({ html: true });
  let renderedMarkdown = md.render(normalizedMarkdown);

  // Add 'd-link' class to all <a> tags
  renderedMarkdown = renderedMarkdown.replace(/<a /g, '<a class="d-link" ');

  return renderedMarkdown;
});
</script>
